<template>
  <div class="category-view">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <van-loading :color="themeColor" size="24px">加载中...</van-loading>
    </div>

    <!-- 分类列表内容 -->
    <div v-else class="category-section">
      <div 
        v-for="(category, index) in categoryList" 
        :key="index" 
        class="category-item"
      >
        <!-- 分类标题 -->
        <div class="main-title">
          <span>{{ category.categoryName }}</span>
          <div 
            @click="navigateToList(category.hasNoChildren ? category.categoryId : category.children[category.activeTab].categoryId, category.categoryName)" 
            class="more-link"
          >
            更多 &gt;
          </div>
        </div>
        
        <!-- 没有子分类的情况 -->
        <div v-if="category.hasNoChildren" class="category-content">
          <div v-if="category.articles && category.articles.length > 0">
            <slot name="list" :data="category.articles" :is-show-time="showTime">
              <List :is-show-time="showTime" :data="category.articles" />
            </slot>
          </div>
          <div v-else class="empty-tip">暂无数据</div>
        </div>

        <!-- 有子分类的情况 -->
        <div v-else class="category-content">
          <slot name="tabs" :category="category" :onChange="handleTabChange">
            <van-tabs v-model="category.activeTab" swipeable>
              <van-tab 
                v-for="(subCategory, subIndex) in category.children" 
                :key="subIndex" 
                :title="subCategory.categoryName"
              >
                <div v-if="subCategory.articles && subCategory.articles.length > 0">
                  <slot name="sub-list" :data="subCategory.articles" :is-show-time="showTime">
                    <List :is-show-time="showTime" :data="subCategory.articles" />
                  </slot>
                </div>
                <div v-else class="empty-tip">暂无数据</div>
              </van-tab>
            </van-tabs>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCategoryData } from "@/request/api";
import List from "@/components/list.vue";

export default {
  name: "CategoryView",
  components: {
    List
  },
  props: {
    // 根分类ID
    categoryId: {
      type: String,
      required: true
    },
    // API路径
    apiPath: {
      type: String,
      default: '/gbs/portal/portal/multiLevelArticle'
    },
    // 是否显示时间
    showTime: {
      type: Boolean,
      default: true
    },
    // 主题颜色
    themeColor: {
      type: String,
      default: '#ee0a24'
    }
  },
  data() {
    return {
      categoryList: [],
      loading: false
    };
  },
  mounted() {
    this.loadCategoryData();
  },
  methods: {
    /**
     * 导航到列表页
     * @param {string|number} categoryId 分类ID
     * @param {string} name 分类名称
     */
    navigateToList(categoryId, name) {
      this.$router.push({
        path: "/newsList",
        query: {
          categoryId: categoryId,
          title: name
        }
      });
    },
    
    /**
     * 处理子分类切换
     * @param {Object} category 分类对象
     * @param {Number} tabIndex 选中的tab索引
     */
    handleTabChange(category, tabIndex) {
      category.activeTab = tabIndex;
    },
    
    /**
     * 加载分类数据
     */
    loadCategoryData() {
      this.loading = true;
      getCategoryData(this.categoryId, this.apiPath)
        .then(res => {
          this.loading = false;
          if (res.code !== 0) {
            this.$Toast && this.$Toast.fail(res.msg);
            return;
          }
          
          // 处理返回的数据
          const categories = res.data.children || [];
          this.categoryList = categories.map(category => {
            // 初始化默认tab
            const activeTab = 0;
            
            // 判断是否有子分类
            const hasNoChildren = !category.children || category.children.length === 0;
            
            return {
              ...category,
              activeTab,
              hasNoChildren
            };
          });
          
          // 数据加载完成后触发事件
          this.$emit('data-loaded', this.categoryList);
        })
        .catch(error => {
          this.loading = false;
          this.$Toast && this.$Toast.fail('加载数据失败');
          console.error('Failed to load category data:', error);
        });
    }
  }
};
</script>

<style scoped lang="scss">
// 加载状态
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  margin-top: 20px;
}

// 分类区域
.category-section {
  padding: 20px 24px;
}

// 分类项
.category-item {
  margin-bottom: 40px;
}

// 主标题样式
.main-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  position: relative;
  border-bottom: 2px solid var(--theme-color, #ee0a24);
  padding-bottom: 12px;
  
  span {
    font-size: 34px;
    color: var(--theme-color, #ee0a24);
    font-weight: 600;
    position: relative;
    padding-left: 10px;
  }
  
  .more-link {
    font-size: 26px;
    color: #666;
    cursor: pointer;
    padding-right: 10px;
  }
}

// 分类内容区域
.category-content {
  margin-top: 20px;
}

// 空状态提示
.empty-tip {
  font-size: 28px;
  line-height: 40px;
  text-align: center;
  color: #999;
  padding: 30px 0;
}

// Tab样式定制
:deep(.van-tabs__wrap) {
  height: 44px;
}

:deep(.van-tabs__nav) {
  background-color: transparent;
}

:deep(.van-tabs__line) {
  background-color: var(--theme-color, #ee0a24);
}

:deep(.van-tab) {
  padding: 0 15px;
  
  &__text {
    font-size: 28px;
    font-weight: 500;
  }
  
  &--active {
    .van-tab__text {
      color: var(--theme-color, #ee0a24);
      font-weight: 600;
    }
  }
}
</style> 